import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Flex, Loading, Text } from 'onefaceui-react-components';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { getResourceTo } from 'react/utils/utils';

import markerImage from 'static/images/map/markers/marker.png';

import { RoutePanel } from './Panels/RoutePanel';
import { DriversPanel } from './Panels/DriversPanel';
import { StopPanel } from './Panels/StopPanel';
import { EquipmentsPanel } from './Panels/EquipmentsPanel';
import { CoordinatesPanel } from './Panels/CoordinatesPanel';
import { TabWrapper } from '../../styles';
import { Container, RouteTabs, MapContent, MapInfos } from './styles';

export function EventInfoPanel({ dataEvent, isFetching }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  const hasCoords = !!dataEvent?.latitude && !!dataEvent?.longitude;

  const createMapAndMarkers = ({ latitude, longitude, stop, event }) => {
    const map = mapInstanceRef.current;
    const baseLayer = Greenmile.Map.Utility.createBaseLayer();
    map.addLayer(baseLayer);

    const markerIcon = window.L.icon({ iconUrl: markerImage });

    const iconPath = require(`static/${event.icon}`);
    const eventIcon = window.L.icon({
      iconUrl: iconPath,
      iconSize: [24, 24]
    });
    window.L.marker([latitude, longitude], { icon: eventIcon }).addTo(map);
    map.lastEventNotification = { latitude, longitude };

    const hasStopCoordinates = stop?.latitude && stop?.longitude;

    if (hasStopCoordinates) {
      window.L.marker([stop.latitude, stop.longitude], {
        icon: markerIcon
      }).addTo(map);

      map.lastEventNotification = {
        latitude: stop.latitude,
        longitude: stop.longitude
      };
    }

    return map;
  };

  const renderMapToolbar = (map) => {
    const toolbar = new Greenmile.Toolbar.Map(map, `.eventDetails .mapEventContainer`, {});

    toolbar.enable([
      toolbar.actions.fitToMarkers,
      toolbar.actions.zoom,
      toolbar.actions.source,
      toolbar.actions.points
    ]);

    toolbar.render();
  };

  const initializeMap = () => {
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = Greenmile.Map.Utility.createDefaultMap(
        mapRef.current
      );
    }

    const map = createMapAndMarkers(dataEvent);
    renderMapToolbar(map);

    new Greenmile.Toolbar.Map.FitToMarkers(
      `#RouteMonitor .event-${dataEvent.id}`,
      map
    ).showAllMarkers();
  };

  useEffect(() => {
    if (mapRef.current && hasCoords) initializeMap();

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [dataEvent, isFetching]);

  return (
    <Container data-testid="event-info-panel">
      <Loading
        message={getResourceTo('loading.message')}
        isVisible={isFetching}
      />
      {hasCoords && !isFetching && (
        <MapContent
          ref={mapRef}
          data-testid="eventdetails-mapa-content"
          className="mapEventContainer mapEventContainerShow"
        />
      )}
      <Flex shouldShow={!isFetching && dataEvent?.event} flexDirection="column">
        <MapInfos>
          <Flex gap={12} alignItems="center">
            {dataEvent?.event?.icon && (
              <img
                src={require(`static/${dataEvent.event.icon}`)}
                alt="Event Icon"
              />
            )}
            <Text className="info-title">{dataEvent?.event?.description}</Text>
          </Flex>
          <Text className="info-sub-title">
            {dataEvent?.event?.eventType?.description}
          </Text>
          <Text className="info-equipment">{dataEvent?.messageBody}</Text>
        </MapInfos>
        <RouteTabs>
          <TabWrapper>
            <Tabs>
              <TabList>
                <Tab>{getResourceTo('eventlog.route')}</Tab>
                <Tab>{getResourceTo('driver')}</Tab>
                <Tab>{getResourceTo('stop')}</Tab>
                <Tab>{getResourceTo('equipment')}</Tab>
                <Tab>{getResourceTo('eventDetails.modal.tab.coordinates')}</Tab>
              </TabList>
              <TabPanel>
                <RoutePanel route={dataEvent?.route} eventId={dataEvent?.id} />
              </TabPanel>
              <TabPanel>
                <DriversPanel driver={dataEvent?.driver} />
              </TabPanel>
              <TabPanel>
                <StopPanel stop={dataEvent?.stop} />
              </TabPanel>
              <TabPanel>
                <EquipmentsPanel
                  equipment={dataEvent?.equipment}
                  eventId={dataEvent?.id}
                />
              </TabPanel>
              <TabPanel>
                <CoordinatesPanel dataEvent={dataEvent} />
              </TabPanel>
            </Tabs>
          </TabWrapper>
        </RouteTabs>
      </Flex>
    </Container>
  );
}

EventInfoPanel.propTypes = {
  dataEvent: PropTypes.object,
  isFetching: PropTypes.bool
};
